import { FileOrLink, IAttachmentFormPayload, IAttachmentTypeListItem, IFileAttachment } from "models/attachments";
import { useEffect, useReducer } from "react";
import { useAbstractWorkItemContext, useWorkItemAttachmentAdd, useWorkItemAttachmentValidation, useWorkItemContext } from "hooks/work-items";
import { useSnackbar } from "@sagecentral/core-ui";
import { AttachmentForm } from "components/attachments";
import { useEditAttachment } from "hooks/attachments";

type WorkItemAttachmentProps = {
    /** Whether the component is in edit mode */
    edit?: boolean;
    /** List of available attachment types */
    types: IAttachmentTypeListItem[] | null;
    /** Function to call when closing the attachment form */
    onClose: () => void;
    /** Function to call to refresh the parent component */
    onRefresh: () => void;
    /** List of existing attachments */
    attachments: IFileAttachment[];
    /** Initial data for editing an existing attachment */
    attachmentData?: IAttachmentFormPayload;
    /** Whether the work item is in draft mode */
    draft?: boolean;
}

const WorkItemAttachment = (props: WorkItemAttachmentProps) => {
    const { edit, types, onClose, onRefresh, attachments, attachmentData } = props;
    const workItemContext = useAbstractWorkItemContext();
    const { workItem } = workItemContext;
    const { displaySnackbar } = useSnackbar();
  
    const [ payload, updatePayload ] = useReducer((state: IAttachmentFormPayload, action: Partial<IAttachmentFormPayload>) => {
        return {
            ...state,
            ...action
        };
    }, { 
        typeId: 0,
        fileOrLink: FileOrLink.File
    });

    useEffect(() => {
        if (attachmentData) {
            updatePayload(attachmentData);
            attachmentData.filePath = undefined;
        }
    }, [ attachmentData ]);

    const validator = useWorkItemAttachmentValidation({
        attachmentId: payload.id,
        workItemId: workItem?.id, 
        typeId: payload.typeId, 
        fileOrLink: payload.fileOrLink,
        draft: props.draft
    });

    const [ handleEdit, editWorking ] = useEditAttachment();
    const [ handleWorkItemAttachmentAdd, addWorking ] = useWorkItemAttachmentAdd();

const handleSave = async (): Promise<void> => {
        if (edit) {
            const result = await handleEdit(workItem?.id ?? 0, payload, props.draft);
            if (result !== null) {
                onRefresh();
                onClose();
            }
        } else {
            const result = await handleWorkItemAttachmentAdd(workItem?.id ?? 0 , payload, props.draft);
            if (result) {                
                onRefresh();
                onClose();
            }
        }
    };

const checkExisting = (payload: IAttachmentFormPayload): boolean => {
        const filenames = payload.files?.map(v => v.name) ?? [];

        if (payload.fileOrLink === FileOrLink.SFTP) {
            filenames.push(payload.name!);
        }
        for (var item of attachments) {
            if (item.workItemInstanceAttachmentID === payload.id) continue;

            if (item.attachmentTypeID === payload.typeId && item.fileOrURLType == payload.fileOrLink){
                // compare without case sensitivity.
                if (item.attachmentName.toLowerCase() === payload.name?.toLowerCase())
                    return true;
                const idx = filenames.findIndex(f => item.attachmentName === f);                
                if (idx !== -1) {
                    return true;
                }
            }

        }
        return false;
    }

    return (
        <AttachmentForm 
            edit={edit}
            saving={addWorking || editWorking}
            payload={payload} 
            updatePayload={updatePayload} 
            validator={validator} 
            types={types} 
            onSave={handleSave} 
            onClose={onClose} 
            onCheckExisting={checkExisting}
            draft={props.draft}
        /> 
    )
};

export default WorkItemAttachment;